/**
 * Payroll — lean tab components for Phases 4/5/6.
 * v109.9.24 — replaces the "Coming Soon" placeholders with functional
 * lean UIs. Full-fidelity polish tracked in the phase queue.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { HandCoins, Wallet, Coffee, Banknote, Plus, Save, CheckCircle2, Calculator, CalendarCheck, XCircle, TrendingUp, Clock, Pencil, AlertTriangle, FileText } from "lucide-react";
import { classifyStatus, statusCellClasses, statusToCode, codeToLabel, fmtHoursHM, computeHoursWorked, isPastOrToday } from "../lib/attendanceStatus";
import { fmtDatePR, fmtTimePR as fmt12h, fmtDateTimePR } from "../lib/datetime";
import { MasterUpdateTag } from "../components/attendance/MasterUpdateTag";
import { UnlockMonthDialog } from "../components/payroll/UnlockMonthDialog";
import { CompOffDecideDialog } from "../components/payroll/CompOffDecideDialog";
import { LeaveAdjustDialog } from "../components/payroll/LeaveAdjustDialog";
import { AmPmTimeInput } from "../components/AmPmTimeInput";
import LeaveStateTag from "../components/LeaveStateTag";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const INR = (v) => `₹${Number(v||0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// -------------------------------------------------------------
// Leave Balance
// -------------------------------------------------------------
export function LeaveBalanceTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [userId, setUserId] = useState("");
  const [users, setUsers] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openingDlg, setOpeningDlg] = useState(null);  // { casual, sick, earned }
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/leave-balances");
      setUsers(data || []);
      if (!userId && (data || [])[0]) setUserId(data[0].id || data[0].employee_id);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const loadLedger = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/leave/balance/monthly?user_id=${userId}&year=${year}`);
      setLedger(data);
    } catch (e) {
      setLedger(null);
      toast.error(formatError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { loadUsers(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadLedger(); /* eslint-disable-next-line */ }, [userId, year]);

  const openOpeningDlg = () =>
    setOpeningDlg(ledger?.opening_january || { casual: 0, sick: 0, earned: 0 });

  const saveOpening = async () => {
    setBusy(true);
    try {
      await api.post("/leave/balance/opening", {
        employee_id: userId, year,
        opening: {
          casual: Number(openingDlg.casual) || 0,
          sick:   Number(openingDlg.sick)   || 0,
          earned: Number(openingDlg.earned) || 0,
        },
      });
      toast.success("Opening balance saved");
      setOpeningDlg(null);
      await loadLedger();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const doCarryForward = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/leave/balance/carry-forward", { employee_id: userId, year });
      toast.success(`Carry-forward from ${year - 1} applied: EL ${data.opening.earned}, CL 0, SL 0`);
      setOpeningDlg(null);
      await loadLedger();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cap = ledger?.carry_forward_cap || { casual: 0, sick: 0, earned: 30 };

  return (
    <div className="space-y-4" data-testid="leave-balance-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <HandCoins className="w-5 h-5 text-emerald-600" />
        <h3 className="text-[15px] font-extrabold text-slate-800 dark:text-slate-100">Leave Balance · Monthly Ledger</h3>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Label className="text-[11px] uppercase tracking-wide">User</Label>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger className="w-56" data-testid="lb-user-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent className="max-h-72 overflow-y-auto">
              {users.map((u) => (
                <SelectItem key={u.id || u.employee_id} value={u.id || u.employee_id}>{u.name} · {u.employee_code || "—"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Label className="text-[11px] uppercase tracking-wide">Year</Label>
          <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24" data-testid="lb-year" />
          <Button size="sm" variant="outline" onClick={loadLedger} className="h-8"><CalendarCheck className="w-4 h-4 mr-1" /> Reload</Button>
        </div>
      </div>

      {/* Carry-forward rules banner */}
      <div className="rounded-lg border border-amber-200 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-700/50 p-2.5 text-[11.5px] text-amber-900 dark:text-amber-100" data-testid="lb-rules">
        <b>Carry-Forward Rules:</b>&nbsp;
        Casual Leave (CL): <b>Not permitted</b> · Sick Leave (SL): <b>Not permitted</b> · Earned Leave (EL): <b>Permitted up to {cap.earned} days</b>
      </div>

      {loading && <div className="p-6 text-center text-slate-400">Loading ledger…</div>}

      {!loading && ledger && (
        <>
          {/* January opening controls */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 flex items-center gap-3 flex-wrap" data-testid="lb-january-controls">
            <div className="text-[13px] font-bold">January {year} · Opening Balance</div>
            <Badge className={ledger.opening_source === "explicit" ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800"} data-testid="lb-opening-source">
              {ledger.opening_source === "explicit" ? "Set by Admin" : "Auto (from prev-year Dec)"}
            </Badge>
            <div className="text-[12px] font-mono">
              CL: <b>{ledger.opening_january?.casual ?? 0}</b> ·
              SL: <b>{ledger.opening_january?.sick ?? 0}</b> ·
              EL: <b>{ledger.opening_january?.earned ?? 0}</b>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={openOpeningDlg} data-testid="lb-set-opening-btn">
                <Pencil className="w-4 h-4 mr-1" /> Set Opening Balance
              </Button>
              <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white"
                onClick={doCarryForward}
                disabled={busy}
                data-testid="lb-carry-forward-btn">
                <TrendingUp className="w-4 h-4 mr-1" /> Fetch from Dec {year - 1}
              </Button>
            </div>
          </div>

          {/* Monthly ledger */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-x-auto" data-testid="lb-monthly-table">
            <table className="w-full text-[12.5px]">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-600">
                <tr>
                  <th className="text-left p-2">Month</th>
                  <th className="text-right p-2">Opening (CL/SL/EL)</th>
                  <th className="text-right p-2">Accrual</th>
                  <th className="text-right p-2">Taken</th>
                  <th className="text-right p-2">Adjusted</th>
                  <th className="text-right p-2">Closing (CL/SL/EL)</th>
                  <th className="text-center p-2">Adjust</th>
                </tr>
              </thead>
              <tbody>
                {(ledger.months || []).map((m) => (
                  <tr key={m.month} className="border-t border-slate-100 dark:border-slate-800" data-testid={`lb-month-${m.month}`}>
                    <td className="p-2 font-semibold">{MONTHS[m.month - 1]}</td>
                    <td className="p-2 text-right font-mono">{m.opening.casual}/{m.opening.sick}/{m.opening.earned}</td>
                    <td className="p-2 text-right font-mono text-emerald-700 dark:text-emerald-400">+{m.accrual.casual}/+{m.accrual.sick}/+{m.accrual.earned}</td>
                    <td className="p-2 text-right font-mono text-rose-700 dark:text-rose-400">
                      -{m.taken.casual}/-{m.taken.sick}/-{m.taken.earned}
                      {m.taken_source === "override" && <span className="ml-1 text-[9px] text-amber-600">(ovr)</span>}
                    </td>
                    <td className="p-2 text-right font-mono text-indigo-700 dark:text-indigo-300" data-testid={`lb-adj-${m.month}`}
                      title={(m.adjustments || []).map((a) => `${a.casual}/${a.sick}/${a.earned} · ${a.remark} — ${a.by_name}`).join("\n")}>
                      {(m.adjustments || []).length ? `${m.adjustment.casual}/${m.adjustment.sick}/${m.adjustment.earned}` : "—"}
                      {(m.adjustments || []).length > 0 && (
                        <div className="text-[9.5px] text-slate-500 font-sans truncate max-w-[180px] ml-auto">{m.adjustments[m.adjustments.length - 1].remark}</div>
                      )}
                    </td>
                    <td className="p-2 text-right font-mono font-bold">{m.closing.casual}/{m.closing.sick}/{m.closing.earned}</td>
                    <td className="p-2 text-center">
                      <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => setAdjustTarget({ employeeId: userId, year, month: m.month })} data-testid={`lb-adjust-btn-${m.month}`}>
                        <Pencil className="w-3 h-3 mr-0.5" /> Adjust
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <LeaveAdjustDialog target={adjustTarget} onClose={() => setAdjustTarget(null)} onDone={loadLedger}
        monthLabel={adjustTarget ? MONTHS[adjustTarget.month - 1] : ""} />
      {/* Set Opening dialog */}
      <Dialog open={!!openingDlg} onOpenChange={(v) => { if (!v) setOpeningDlg(null); }}>
        <DialogContent className="max-w-md" data-testid="lb-opening-dialog">
          <DialogHeader>
            <DialogTitle>Set Opening Balance · Jan {year}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div className="text-[11px] text-slate-500 mb-2">
              Direct entry — bypasses carry-forward from previous year. Use only if you have not run the carry-forward.
            </div>
            {["casual","sick","earned"].map((k) => (
              <div key={k} className="flex items-center gap-2">
                <Label className="w-16 uppercase text-[11px]">{k}</Label>
                <Input type="number" step="0.5" min="0" value={openingDlg?.[k] ?? 0}
                  onChange={(e) => setOpeningDlg({ ...openingDlg, [k]: parseFloat(e.target.value) || 0 })}
                  data-testid={`lb-opening-${k}`}/>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpeningDlg(null)}>Cancel</Button>
            <Button onClick={saveOpening} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="lb-opening-save">
              <Save className="w-4 h-4 mr-1.5" /> {busy ? "Saving…" : "Save Opening"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// -------------------------------------------------------------
// Salary Advance (extends existing /salary/advances)
// -------------------------------------------------------------
export function SalaryAdvanceTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [schedule, setSchedule] = useState(null);   // { adv, installments, start_year, start_month }
  const [editing, setEditing]   = useState(null);   // { adv, amount, reason, month }
  const [reviewing, setReviewing] = useState(null); // { adv, action: "approve"|"reject", remark }
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/salary/advances");
      setRows(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveSchedule = async () => {
    if (!schedule) return;
    try {
      await api.patch(`/salary/advances/${schedule.adv.id}/schedule`, {
        installments: schedule.installments,
        start_year: schedule.start_year,
        start_month: schedule.start_month,
      });
      toast.success("Recovery plan saved");
      setSchedule(null);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.patch(`/salary/advances/${editing.adv.id}`, {
        amount: Number(editing.amount),
        reason: editing.reason,
        month:  editing.month,
      });
      toast.success("Advance updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const submitReview = async () => {
    if (!reviewing) return;
    try {
      const url = `/salary/advances/${reviewing.adv.id}/${reviewing.action}`;
      await api.post(url, { remark: (reviewing.remark || "").trim() });
      toast.success(reviewing.action === "approve" ? "Advance approved" : "Advance rejected");
      setReviewing(null);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="salary-advance-tab">
      <div className="flex items-center gap-2">
        <Wallet className="w-5 h-5 text-sky-600" />
        <h3 className="text-[15px] font-extrabold">Salary Advance</h3>
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-600">
            <tr>
              <th className="text-left p-2">Employee</th>
              <th className="text-right p-2">Amount</th>
              <th className="text-left p-2">Month</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Reason</th>
              <th className="text-center p-2">Recovery Plan</th>
              <th className="text-center p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="p-6 text-center text-slate-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-slate-400">No advances filed yet.</td></tr>}
            {!loading && rows.map((r) => {
              const isPending = String(r.status || "").toLowerCase() === "pending";
              return (
                <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`advance-row-${r.id}`}>
                  <td className="p-2 font-semibold">{r.employee_name}</td>
                  <td className="p-2 text-right font-mono">{INR(r.amount)}</td>
                  <td className="p-2 font-mono text-[11px]">{r.month || "—"}</td>
                  <td className="p-2"><Badge className={r.status === "Approved" ? "bg-emerald-100 text-emerald-800" : r.status === "Rejected" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"} data-testid={`advance-status-${r.id}`}>{r.status}</Badge></td>
                  <td className="p-2 text-slate-500">{r.reason}</td>
                  <td className="p-2 text-center">
                    {r.recovery_plan?.length ? (
                      <Badge className="bg-sky-100 text-sky-800">{r.recovery_plan.length} installments</Badge>
                    ) : r.status === "Approved" ? (
                      <Button size="sm" variant="outline" className="h-6 text-[10.5px]"
                        onClick={() => setSchedule({ adv: r, installments: 3, start_year: new Date().getFullYear(), start_month: new Date().getMonth()+1 })}
                        data-testid={`advance-schedule-${r.id}`}
                      >Set Plan</Button>
                    ) : "—"}
                  </td>
                  <td className="p-2 text-center">
                    <div className="flex items-center justify-center gap-1 flex-wrap">
                      {isPending && (
                        <>
                          <Button size="sm" className="h-6 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => setReviewing({ adv: r, action: "approve", remark: "" })}
                            data-testid={`advance-approve-${r.id}`}
                          >Approve</Button>
                          <Button size="sm" variant="outline" className="h-6 text-[10.5px] text-rose-600 border-rose-300 hover:bg-rose-50"
                            onClick={() => setReviewing({ adv: r, action: "reject", remark: "" })}
                            data-testid={`advance-reject-${r.id}`}
                          >Reject</Button>
                          <Button size="sm" variant="outline" className="h-6 text-[10.5px]"
                            onClick={() => setEditing({ adv: r, amount: r.amount, reason: r.reason || "", month: r.month || "" })}
                            data-testid={`advance-edit-${r.id}`}
                          ><Pencil className="w-3 h-3 mr-1" />Edit</Button>
                        </>
                      )}
                      {!isPending && (
                        <span className="text-[10.5px] text-slate-500 italic">{r.reviewed_by_name || "—"}</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Approve / Reject dialog */}
      <Dialog open={!!reviewing} onOpenChange={(v) => { if (!v) setReviewing(null); }}>
        <DialogContent className="max-w-md" data-testid="advance-review-dialog">
          <DialogHeader>
            <DialogTitle>
              {reviewing?.action === "approve" ? "Approve Salary Advance" : "Reject Salary Advance"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-[12.5px]">
            <div><b>Employee:</b> {reviewing?.adv?.employee_name}</div>
            <div><b>Amount:</b> <span className="font-mono">{INR(reviewing?.adv?.amount)}</span></div>
            <div><b>Reason:</b> {reviewing?.adv?.reason || "—"}</div>
            <div>
              <Label>Remark {reviewing?.action === "reject" && <span className="text-rose-500">*</span>}</Label>
              <Textarea rows={3} value={reviewing?.remark || ""}
                onChange={(e) => setReviewing({ ...reviewing, remark: e.target.value })}
                placeholder={reviewing?.action === "reject" ? "Reason for rejection (min 3 chars)" : "Optional remark"}
                data-testid="advance-review-remark"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewing(null)}>Cancel</Button>
            <Button onClick={submitReview}
              className={reviewing?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}
              data-testid="advance-review-submit"
            >
              Confirm {reviewing?.action === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-md" data-testid="advance-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Advance — {editing?.adv?.employee_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label>Amount (₹)</Label>
              <Input type="number" min="1" step="0.01" value={editing?.amount || 0}
                onChange={(e) => setEditing({ ...editing, amount: Number(e.target.value) })}
                data-testid="advance-edit-amount"/>
            </div>
            <div>
              <Label>Month (YYYY-MM)</Label>
              <Input value={editing?.month || ""}
                onChange={(e) => setEditing({ ...editing, month: e.target.value })}
                placeholder="2026-07"
                data-testid="advance-edit-month"/>
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea rows={3} value={editing?.reason || ""}
                onChange={(e) => setEditing({ ...editing, reason: e.target.value })}
                data-testid="advance-edit-reason"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="advance-edit-save">
              <Save className="w-4 h-4 mr-1.5" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!schedule} onOpenChange={(v) => { if (!v) setSchedule(null); }}>
        <DialogContent className="max-w-md" data-testid="advance-plan-dialog">
          <DialogHeader>
            <DialogTitle>Recovery Plan — {schedule?.adv?.employee_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label>Installments</Label>
              <Input type="number" min="1" max="60" value={schedule?.installments || 1}
                onChange={(e) => setSchedule({ ...schedule, installments: Number(e.target.value) })}
                data-testid="advance-plan-count"/>
              <div className="text-[10.5px] text-slate-500 mt-1">
                {schedule && `Per installment ≈ ${INR((schedule.adv?.amount || 0) / (schedule.installments || 1))}`}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Start Month</Label>
                <Select value={String(schedule?.start_month || 1)} onValueChange={(v) => setSchedule({ ...schedule, start_month: Number(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m,i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Start Year</Label>
                <Input type="number" value={schedule?.start_year || 2026}
                  onChange={(e) => setSchedule({ ...schedule, start_year: Number(e.target.value) })}/>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedule(null)}>Cancel</Button>
            <Button onClick={saveSchedule} className="bg-emerald-600 hover:bg-emerald-700"><Save className="w-4 h-4 mr-1.5" /> Save Plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// -------------------------------------------------------------
// Comp Off
// -------------------------------------------------------------
export function CompOffTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get(`/payroll/comp-off/summary?year=${year}`)
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(formatError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [year]);
  const monLabel = (ym) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
  return (
    <div className="space-y-4" data-testid="comp-off-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Coffee className="w-5 h-5 text-purple-600" />
        <h3 className="text-[15px] font-extrabold">Comp Off · Credited Balance</h3>
        <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24 ml-auto" data-testid="compoff-year" />
      </div>
      <div className="text-[11.5px] text-slate-500 italic">Approve / disapprove comp-offs from the Attendance tab. Only approved comp-offs are counted here, in the month they are credited.</div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-300">
            <tr>
              <th className="text-left p-2">Employee</th>
              <th className="text-right p-2">Comp-Off Count</th>
              <th className="text-left p-2">Credited In</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="3" className="p-6 text-center text-slate-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="3" className="p-6 text-center text-slate-400">No approved comp-offs in {year}.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.employee_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`compoff-row-${r.employee_id}`}>
                <td className="p-2 font-semibold">{r.employee_name}</td>
                <td className="p-2 text-right font-mono font-bold text-purple-700 dark:text-purple-300" data-testid={`compoff-count-${r.employee_id}`}>{r.total_days} day(s)</td>
                <td className="p-2">
                  <div className="flex flex-wrap gap-1">
                    {r.months.map((m) => (
                      <span key={m.month} className="rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200 px-2 py-0.5 text-[10.5px] font-semibold" title={m.dates.map(fmtDatePR).join(", ")}>
                        {monLabel(m.month)} · {m.days}d
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// -------------------------------------------------------------
// Accounts (voucher entry)
// -------------------------------------------------------------
export function AccountsTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(null);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/payroll/accounts/vouchers?year=${year}&month=${month}`);
      setRows(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const [users, setUsers] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/employees");
        setUsers((data || []).map((e) => ({ id: e.id, name: e.name })));
      } catch { /* ignore */ }
    })();
  }, []);

  const createVoucher = async () => {
    if (!creating?.employee_id || !creating?.amount || !creating?.voucher_no || !creating?.voucher_date) {
      toast.error("Fill all required fields"); return;
    }
    try {
      await api.post("/payroll/accounts/vouchers", {
        ...creating,
        year, month,
        employee_name: users.find((u) => u.id === creating.employee_id)?.name || "",
      });
      toast.success("Voucher recorded");
      setCreating(null);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const markDisbursed = async (v) => {
    if (!window.confirm(`Mark ${v.voucher_no} as disbursed?`)) return;
    try {
      await api.post(`/payroll/accounts/vouchers/${v.id}/disburse`, {});
      toast.success("Disbursed");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="accounts-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Banknote className="w-5 h-5 text-emerald-600" />
        <h3 className="text-[15px] font-extrabold">Accounts · Voucher Entry</h3>
        <div className="ml-auto flex items-center gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24" />
          <Button onClick={() => setCreating({ kind: "salary", employee_id: "", amount: "", voucher_no: "", voucher_date: new Date().toISOString().slice(0,10), payment_mode: "NEFT" })}
            className="bg-emerald-600 hover:bg-emerald-700" data-testid="accounts-new-voucher">
            <Plus className="w-4 h-4 mr-1.5" /> New Voucher
          </Button>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-600">
            <tr>
              <th className="text-left p-2">Voucher #</th>
              <th className="text-left p-2">Date</th>
              <th className="text-left p-2">Employee</th>
              <th className="text-left p-2">Kind</th>
              <th className="text-right p-2">Amount</th>
              <th className="text-left p-2">Payment</th>
              <th className="text-left p-2">Status</th>
              <th className="text-center p-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" className="p-6 text-center text-slate-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-slate-400">No vouchers for this month.</td></tr>}
            {!loading && rows.map((v) => (
              <tr key={v.id} className="border-t border-slate-100 dark:border-slate-800">
                <td className="p-2 font-mono">{v.voucher_no}</td>
                <td className="p-2 text-slate-500">{fmtDatePR(v.voucher_date)}</td>
                <td className="p-2 font-semibold">{v.employee_name}</td>
                <td className="p-2"><Badge className={v.kind === "salary" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{v.kind}</Badge></td>
                <td className="p-2 text-right font-mono">{INR(v.amount)}</td>
                <td className="p-2 text-[11px] text-slate-500">{v.payment_mode} {v.payment_reference && `· ${v.payment_reference}`}</td>
                <td className="p-2"><Badge className={v.status === "disbursed" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}>{v.status}</Badge></td>
                <td className="p-2 text-center">
                  {v.status !== "disbursed" && (
                    <Button size="sm" variant="outline" className="h-6 text-[10.5px]" onClick={() => markDisbursed(v)}
                      data-testid={`voucher-disburse-${v.id}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Disbursed
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!creating} onOpenChange={(v) => { if (!v) setCreating(null); }}>
        <DialogContent className="max-w-lg" data-testid="voucher-create-dialog">
          <DialogHeader><DialogTitle>New Voucher · {MONTHS[month-1]} {year}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div>
              <Label>Employee</Label>
              <Select value={creating?.employee_id || ""} onValueChange={(v) => setCreating({ ...creating, employee_id: v })}>
                <SelectTrigger data-testid="voucher-emp"><SelectValue placeholder="Pick employee" /></SelectTrigger>
                <SelectContent className="max-h-72">{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Kind</Label>
                <Select value={creating?.kind || "salary"} onValueChange={(v) => setCreating({ ...creating, kind: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="salary">Salary</SelectItem>
                    <SelectItem value="advance">Advance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount ₹</Label>
                <Input type="number" value={creating?.amount || ""} onChange={(e) => setCreating({ ...creating, amount: e.target.value })} data-testid="voucher-amount"/>
              </div>
              <div>
                <Label>Voucher No.</Label>
                <Input value={creating?.voucher_no || ""} onChange={(e) => setCreating({ ...creating, voucher_no: e.target.value })} data-testid="voucher-no"/>
              </div>
              <div>
                <Label>Voucher Date</Label>
                <Input type="date" value={creating?.voucher_date || ""} onChange={(e) => setCreating({ ...creating, voucher_date: e.target.value })}/>
              </div>
              <div>
                <Label>Payment Mode</Label>
                <Input value={creating?.payment_mode || ""} onChange={(e) => setCreating({ ...creating, payment_mode: e.target.value })}/>
              </div>
              <div>
                <Label>Reference</Label>
                <Input value={creating?.payment_reference || ""} onChange={(e) => setCreating({ ...creating, payment_reference: e.target.value })}/>
              </div>
            </div>
            <div>
              <Label>Remarks</Label>
              <Input value={creating?.remarks || ""} onChange={(e) => setCreating({ ...creating, remarks: e.target.value })}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(null)}>Cancel</Button>
            <Button onClick={createVoucher} className="bg-emerald-600 hover:bg-emerald-700" data-testid="voucher-save">
              <Save className="w-4 h-4 mr-1.5" /> Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// -------------------------------------------------------------
// Salary Calc — per-day formula engine
// -------------------------------------------------------------
export function SalaryCalcTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [empId, setEmpId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [calc, setCalc] = useState(null);
  const [loading, setLoading] = useState(false);
  // v109.9.129 — Salary Calc gate: needs period.state === 'finalised'.
  const [period, setPeriod] = useState(null);
  // v109.9.131 — Admin can click a deduction-status tile to "pay" that day
  // (reverse its deduction). This state holds the ISO dates currently
  // marked as paid; it's sent to the backend so the formula updates live.
  const [paidDates, setPaidDates] = useState(new Set());
  // v109.9.137 — Interactive per-day amount overrides + locked-salary list.
  const [dayAmts, setDayAmts] = useState({});  // date → {paid_amount, deducted_amount}
  const [lockedList, setLockedList] = useState({ items: [], grand_total: 0, count: 0 });
  const [lockBusy, setLockBusy] = useState(false);
  // v109.9.150 — Lock confirmation dialog state
  const [lockConfirm, setLockConfirm] = useState({ open: false, total: 0, rows: [] });
  const [salUnlockOpen, setSalUnlockOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/employees");
        const list = (data || []).filter((e) => e.id).map((e) => ({ id: e.id, name: e.name, salary: e.salary || 0 }));
        setEmployees(list);
        if (list.length && !empId) setEmpId(list[0].id);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (!empId) return;
    setLoading(true);
    try {
      const pdParam = Array.from(paidDates).sort().join(",");
      const [c, p, locked] = await Promise.all([
        api.get(`/payroll/salary-calc?employee_id=${empId}&year=${year}&month=${month}&paid_dates=${encodeURIComponent(pdParam)}`),
        api.get(`/payroll/attendance/period?employee_id=${empId}&year=${year}&month=${month}`),
        api.get(`/payroll/salary-calc/locked?year=${year}&month=${month}`).catch(() => ({ data: { items: [], grand_total: 0, count: 0 } })),
      ]);
      setCalc(c.data);
      setPeriod(p.data);
      setLockedList(locked.data || { items: [], grand_total: 0, count: 0 });
      // Seed dayAmts from backend so the tile inputs show the persisted values.
      const seed = {};
      for (const row of c.data?.per_day || []) {
        seed[row.date] = {
          paid_amount: Number(row.paid_amount ?? row.default_paid_amount ?? 0),
          deducted_amount: Number(row.deducted_amount ?? row.default_deducted_amount ?? 0),
        };
      }
      setDayAmts(seed);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  // Reset the paid-dates selection whenever employee/month changes.
  useEffect(() => { setPaidDates(new Set()); }, [empId, year, month]);
  useEffect(() => { if (empId) load(); /* eslint-disable-next-line */ }, [empId, year, month, paidDates]);

  const toggleDayPaid = (row) => {
    if (!row?.is_deduction_candidate) return;
    setPaidDates((s) => {
      const next = new Set(s);
      if (next.has(row.date)) next.delete(row.date); else next.add(row.date);
      return next;
    });
  };

  const c = calc?.counts || {};
  return (
    <div className="space-y-4" data-testid="salary-calc-tab">
      <UnlockMonthDialog
        open={salUnlockOpen} onClose={() => setSalUnlockOpen(false)}
        employeeId={empId} year={year} month={month} onUnlocked={load}
        endpoint="/payroll/salary-calc/unlock" title="Unlock Salary"
        description="Enter the payroll unlock password to edit this salary amount and resubmit (lock again)."
        successMsg="Salary unlocked — edit the amounts and click Review & Lock Salary to resubmit"
        testid="sc-unlock"
      />
      <div className="flex items-center gap-2 flex-wrap">
        <Calculator className="w-5 h-5 text-emerald-600" />
        <h3 className="text-[15px] font-extrabold">Salary Calculation</h3>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Select value={empId} onValueChange={setEmpId}>
            <SelectTrigger className="w-56" data-testid="salary-calc-emp"><SelectValue placeholder="Pick employee" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24" />
        </div>
      </div>

      {loading && <div className="p-6 text-center text-slate-400">Computing…</div>}

      {/* v109.9.129 — Gate: Salary Calc must not proceed until Attendance is Finalised. */}
      {!loading && period && period.state !== "finalised" && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/60 p-5 text-center" data-testid="salary-calc-locked">
          <div className="text-[14.5px] font-extrabold text-amber-900 dark:text-amber-100 mb-1">
            🔒 Salary Calculation Locked
          </div>
          <div className="text-[12.5px] text-amber-800 dark:text-amber-200 leading-relaxed max-w-lg mx-auto">
            This month is in state <b className="font-mono">{period.state || "—"}</b>. Salary can only be calculated after
            the employee has clicked <b>Calculate My Salary</b> and an admin has clicked <b>Finalize for Salary</b>
            in the Attendance tab.
          </div>
        </div>
      )}

      {!loading && calc && period?.state === "finalised" && (
        <>
          {/* v109.9.132 — Set Monthly Salary inline when it's missing. */}
          {(!calc.employee?.monthly_salary || calc.employee.monthly_salary <= 0) && (
            <MissingSalaryPanel
              employeeId={empId}
              employeeName={calc.employee?.name || employees.find((e) => e.id === empId)?.name}
              onSaved={() => load()}
            />
          )}

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="salary-calc-summary">
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
              <div className="text-[10px] uppercase text-slate-500 font-bold">Monthly Salary (MS)</div>
              <div className="text-[18px] font-extrabold text-slate-800 dark:text-slate-100">{INR(calc.employee.monthly_salary)}</div>
              <div className="text-[10px] text-slate-400">Daily = MS ÷ {calc.total_days_in_month || calc.calendar_days} = {INR(calc.per_day_rate)}</div>
            </div>
            <div className="rounded-xl border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/20 p-3">
              <div className="text-[10px] uppercase text-rose-800 dark:text-rose-300 font-bold">Deduction</div>
              <div className="text-[18px] font-extrabold text-rose-900 dark:text-rose-100">− {INR(calc.formula?.deduction ?? 0)}</div>
              <div className="text-[10px] text-rose-700 dark:text-rose-300">
                A {calc.deducted_counts?.A ?? 0} · LWP {calc.deducted_counts?.LWP ?? 0} · HD-A {calc.deducted_counts?.["HD-A"] ?? 0} · HD-LWP {calc.deducted_counts?.["HD-LWP"] ?? 0} → {calc.formula?.deduction_days ?? 0}d × {INR(calc.per_day_rate)}
              </div>
            </div>
            <div className="rounded-xl border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/20 p-3">
              <div className="text-[10px] uppercase text-sky-800 dark:text-sky-300 font-bold">Incentives</div>
              <div className="text-[18px] font-extrabold text-sky-900 dark:text-sky-100">+ {INR(calc.incentives ?? 0)}</div>
              <div className="text-[10px] text-sky-700 dark:text-sky-300">Approved this month</div>
            </div>
            <div className="rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20 p-3">
              <div className="text-[10px] uppercase text-amber-800 dark:text-amber-300 font-bold">Net Payable</div>
              <div className="text-[22px] font-extrabold text-amber-900 dark:text-amber-100">{INR(calc.net)}</div>
              <div className="text-[10px] text-amber-700 dark:text-amber-300">
                MS − Ded + Inc − Adv({INR(calc.advance_recovery_due)})
              </div>
            </div>
          </div>

          {/* v109.9.131 — Formula strip (new spec) */}
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60 p-2.5 text-[11px] font-mono text-slate-700 dark:text-slate-200" data-testid="salary-calc-formula-strip">
            <div>DS = MS ÷ Days-in-Month = {INR(calc.employee.monthly_salary)} ÷ {calc.total_days_in_month || calc.calendar_days} = <b>{INR(calc.per_day_rate)}</b></div>
            <div>Deduction = ((A + LWP)×1 + (HD-A + HD-LWP)×0.5) × DS</div>
            <div className="text-slate-500">
              = (({calc.deducted_counts?.A ?? 0} + {calc.deducted_counts?.LWP ?? 0})×1 + ({calc.deducted_counts?.["HD-A"] ?? 0} + {calc.deducted_counts?.["HD-LWP"] ?? 0})×0.5) × {INR(calc.per_day_rate)} = <b className="text-rose-700 dark:text-rose-300">{INR(calc.formula?.deduction ?? 0)}</b>
            </div>
            <div>Net = MS − Deduction + Incentives − Adv Recovery = <b className="text-emerald-700 dark:text-emerald-300">{INR(calc.net)}</b></div>
          </div>

          {/* v109.9.137 — Auto-fetched per-code counts from the Attendance tab */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3" data-testid="salary-calc-counts">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <div className="text-[12.5px] font-bold text-slate-800 dark:text-slate-100">Attendance counts · {MONTHS[month - 1]} {year}</div>
              <span className="text-[10.5px] text-slate-500">Auto-fetched from Attendance tab (recompute via <b>Recheck Status</b> there).</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {["P","A","HD-A","WO","PH","CL","SL","EL","LWP","HD-CL","HD-SL","HD-EL","HD-LWP"].map((code) => {
                const cnt = (calc.counts_by_code || {})[code] || 0;
                const isPaid = ["P","WO","PH","CL","SL","EL","HD-CL","HD-SL","HD-EL"].includes(code);
                const isDed  = ["A","LWP","HD-A","HD-LWP"].includes(code);
                const cls = isPaid
                  ? "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-200 dark:border-emerald-500/40"
                  : isDed
                  ? "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-500/20 dark:text-rose-200 dark:border-rose-500/40"
                  : "bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-200";
                return (
                  <div key={code} className={`px-2 py-1 rounded border text-[10.5px] font-bold ${cls}`} data-testid={`sc-count-${code}`}>
                    <span className="font-mono">{code}</span>
                    <span className="mx-1 opacity-60">·</span>
                    <span>{cnt}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* v109.9.137 — Interactive Days-of-Month tile grid (paid green / deducted red) */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3" data-testid="salary-calc-tile-grid">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <div className="text-[12.5px] font-bold text-slate-800 dark:text-slate-100">Days of the month</div>
              <span className="text-[10.5px] text-slate-500">
                Green = Paid · Red = Deducted · Grey = Neutral. Edit any amount below — Paid ≤ DS, Deducted ≥ 0.
              </span>
              <span className="ml-auto text-[11px] text-slate-600 dark:text-slate-300">
                DS = <b className="font-mono text-emerald-700 dark:text-emerald-300">{INR(calc.per_day_rate)}</b>
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
              {(calc.per_day || []).map((d) => {
                const kind = d.tile_kind || "neutral";
                const paid = Number(dayAmts[d.date]?.paid_amount ?? d.paid_amount ?? 0);
                const ded  = Number(dayAmts[d.date]?.deducted_amount ?? d.deducted_amount ?? 0);
                const ds   = Number(calc.per_day_rate || 0);
                const isLocked = !!calc.salary_lock?.locked;
                // v109.9.139 — Click the tile body to toggle Paid ⇄ Unpaid.
                // Effective kind = current live amounts (respects user edits).
                const effectiveKind =
                  paid > 0 && ded === 0 ? "paid" :
                  ded > 0 && paid === 0 ? "deducted" :
                  paid === 0 && ded === 0 ? "neutral" : kind;
                const tileCls =
                  effectiveKind === "paid"     ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-600" :
                  effectiveKind === "deducted" ? "border-rose-400 bg-rose-50 dark:bg-rose-950/30 dark:border-rose-600" :
                                                 "border-slate-200 bg-slate-50 dark:bg-slate-900 dark:border-slate-700";
                const halfWeight = (d.status_code === "HD-A" || d.status_code === "HD-LWP") ? 0.5 : 1.0;
                const toggle = () => {
                  if (isLocked) return;
                  if (effectiveKind === "paid") {
                    // Paid → Unpaid: deducted = DS × weight (0.5 for half-day)
                    setDayAmts((s) => ({ ...s, [d.date]: { paid_amount: 0, deducted_amount: Number((ds * halfWeight).toFixed(2)) } }));
                  } else {
                    // Deducted / Neutral → Paid: paid = DS
                    setDayAmts((s) => ({ ...s, [d.date]: { paid_amount: Number(ds.toFixed(2)), deducted_amount: 0 } }));
                  }
                };
                // v109.9.150 — Per-day pay for the running total (non-negative)
                const dayPay = effectiveKind === "paid"
                  ? paid
                  : effectiveKind === "deducted"
                    ? Math.max(0, ds - ded)
                    : 0;
                // v109.9.150 — Half-pay toggle: mark deduction as DS/2.
                const isHalfPay = effectiveKind === "deducted" && Math.abs(ded - ds / 2) < 0.01;
                const toggleHalfPay = (ev) => {
                  ev.stopPropagation();
                  if (isLocked) return;
                  if (isHalfPay) {
                    // Was DS/2 deducted → flip to full deduction (DS).
                    setDayAmts((s) => ({ ...s, [d.date]: { paid_amount: 0, deducted_amount: Number(ds.toFixed(2)) } }));
                  } else {
                    // Full or partial deduction → mark half-pay (deducted = DS/2).
                    setDayAmts((s) => ({ ...s, [d.date]: { paid_amount: 0, deducted_amount: Number((ds / 2).toFixed(2)) } }));
                  }
                };
                return (
                  <div
                    key={d.date}
                    className={`rounded-lg border-2 p-2 ${tileCls} ${isLocked ? "opacity-70 cursor-not-allowed" : "cursor-pointer hover:ring-2 hover:ring-slate-300 dark:hover:ring-slate-600 transition"}`}
                    onClick={toggle}
                    role="button"
                    tabIndex={isLocked ? -1 : 0}
                    title={isLocked ? "Salary is locked" : "Click to toggle Paid ⇄ Unpaid"}
                    data-testid={`sc-tile-${d.date}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[10.5px] font-mono font-bold">{d.date.slice(8, 10)}</div>
                      <div className="text-[9px] uppercase font-extrabold tracking-wide font-mono">{d.status_code || "-"}</div>
                    </div>
                    <div className="text-[9px] text-slate-500 mb-1">DS · <span className="font-mono">{INR(ds)}</span></div>
                    {effectiveKind === "paid" && (
                      <div>
                        <div className="text-[8.5px] text-emerald-800 dark:text-emerald-200 font-bold uppercase mb-0.5">Paid</div>
                        <Input
                          type="number" step="0.01" min={0} max={ds}
                          value={paid}
                          disabled={isLocked}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            const raw = Number(e.target.value || 0);
                            const clamped = Math.max(0, Math.min(ds, raw));
                            setDayAmts((s) => ({ ...s, [d.date]: { paid_amount: clamped, deducted_amount: 0 } }));
                          }}
                          className="h-7 text-[10.5px] font-mono px-1.5"
                          data-testid={`sc-tile-paid-${d.date}`}
                        />
                      </div>
                    )}
                    {effectiveKind === "deducted" && (
                      <div>
                        <div className="flex items-center justify-between mb-0.5">
                          <div className="text-[8.5px] text-rose-800 dark:text-rose-200 font-bold uppercase">Deducted</div>
                          {/* v109.9.150 — Half-pay toggle */}
                          {!isLocked && (
                            <button
                              onClick={toggleHalfPay}
                              className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[8px] font-bold border transition ${
                                isHalfPay
                                  ? "bg-amber-500 text-white border-amber-600"
                                  : "bg-white dark:bg-slate-800 text-amber-700 dark:text-amber-300 border-amber-300"
                              }`}
                              title={isHalfPay ? "Half-pay ON (deduction = DS/2) — click to make full deduction" : "Mark half-pay (deduction = DS/2)"}
                              data-testid={`sc-tile-halfpay-${d.date}`}
                            >
                              ½·Pay
                            </button>
                          )}
                        </div>
                        <Input
                          type="number" step="0.01" min={0}
                          value={ded}
                          disabled={isLocked}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            const raw = Number(e.target.value || 0);
                            const clamped = Math.max(0, raw);
                            setDayAmts((s) => ({ ...s, [d.date]: { paid_amount: 0, deducted_amount: clamped } }));
                          }}
                          className="h-7 text-[10.5px] font-mono px-1.5"
                          data-testid={`sc-tile-ded-${d.date}`}
                        />
                        {/* Show computed day-pay for deducted tile */}
                        <div className="text-[8.5px] text-slate-500 mt-0.5 font-mono">Pay: <b className="text-emerald-700 dark:text-emerald-300">{INR(dayPay)}</b></div>
                      </div>
                    )}
                    {effectiveKind === "neutral" && (
                      <div className="text-[9.5px] text-slate-500 font-mono">click to pay</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Live total + Lock button */}
            {(() => {
              // v109.9.150 — Non-negative Monthly Salary = Σ day_pay per user spec.
              const ds = Number(calc.per_day_rate || 0);
              const _paidCodes = new Set(["P", "WO", "PH", "CL", "SL", "EL", "HD-CL", "HD-SL", "HD-EL"]);
              const _dedCodes  = new Set(["A", "LWP", "HD-A", "HD-LWP"]);
              const perDayTotals = (calc.per_day || []).map((d) => {
                const p = Number(dayAmts[d.date]?.paid_amount ?? d.paid_amount ?? 0);
                const dd = Number(dayAmts[d.date]?.deducted_amount ?? d.deducted_amount ?? 0);
                const code = d.status_code || "";
                const effP = p > 0 && dd === 0;
                const effD = dd > 0 && p === 0;
                if (effP || _paidCodes.has(code)) return p;
                if (effD || _dedCodes.has(code)) return Math.max(0, ds - dd);
                return 0;
              });
              const total = perDayTotals.reduce((a, b) => a + b, 0);
              const isLocked = !!calc.salary_lock?.locked;
              const prevSal = calc.previous_month_salary;
              return (
                <div className="mt-3 flex items-center gap-3 flex-wrap border-t border-slate-200 dark:border-slate-700 pt-3">
                  <div className="text-[11px] text-slate-600 dark:text-slate-300">
                    <b>Monthly Salary =</b> <span className="font-mono">Σ Day Pay</span> · Paid day → DS · Deducted day → max(0, DS − Deducted) · Neutral → 0
                  </div>
                  <div className="ml-auto flex items-center gap-2 flex-wrap">
                    <div className="text-[12px] text-slate-600 dark:text-slate-300">Monthly Salary =</div>
                    <div className="text-[18px] font-extrabold text-emerald-700 dark:text-emerald-300 font-mono" data-testid="sc-monthly-total">{INR(total)}</div>
                    {isLocked ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-200" data-testid="sc-locked-badge">
                        🔒 Locked · {calc.salary_lock?.locked_by_name || ""}
                      </Badge>
                    ) : null}
                    {isLocked ? (
                      <Button size="sm" variant="outline" onClick={() => setSalUnlockOpen(true)} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="sc-unlock-btn">
                        Unlock (Password)
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setLockConfirm({ open: true, total, rows: (calc.per_day || []).map((d) => ({
                          date: d.date,
                          status_code: d.status_code,
                          paid_amount: Number(dayAmts[d.date]?.paid_amount ?? d.paid_amount ?? 0),
                          deducted_amount: Number(dayAmts[d.date]?.deducted_amount ?? d.deducted_amount ?? 0),
                        })) })}
                        disabled={lockBusy}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="sc-lock-btn"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1.5" /> Review & Lock Salary
                      </Button>
                    )}
                  </div>
                  {/* v109.9.150 — Previous-month comparison + "Change it" */}
                  {isLocked && prevSal && (
                    <div className="w-full mt-2 rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/25 px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="sc-prev-month-block">
                      <div className="text-[11px] font-bold text-sky-800 dark:text-sky-200">
                        Previous Month ({MONTHS[(prevSal.month || 1) - 1]} {prevSal.year}):
                      </div>
                      <div className="text-[13.5px] font-extrabold text-sky-900 dark:text-sky-100 font-mono">{INR(prevSal.calculated_salary || 0)}</div>
                      <Badge className={prevSal.locked ? "bg-emerald-100 text-emerald-800 text-[10px]" : "bg-amber-100 text-amber-800 text-[10px]"}>
                        {prevSal.locked ? "Locked" : "Not Locked"}
                      </Badge>
                      <button
                        onClick={() => setMonth(prevSal.month)}
                        className="ml-auto text-[10.5px] font-bold text-sky-700 dark:text-sky-300 underline hover:text-sky-900 dark:hover:text-sky-100"
                        data-testid="sc-prev-month-change-btn"
                      >
                        Change it →
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Per-day breakdown */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-600">
                <tr>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Day</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Punch In</th>
                  <th className="text-left p-2">Punch Out</th>
                  <th className="text-right p-2">Hrs</th>
                  <th className="text-right p-2">Wt</th>
                  <th className="text-right p-2">Day Pay</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const visible = calc.per_day.filter(isPastOrToday);
                  return visible.length > 0 ? visible : calc.per_day;
                })().map((d) => (
                  <tr key={d.date} className={`border-t border-slate-100 dark:border-slate-800 ${d.is_sunday ? "bg-purple-50/40 dark:bg-purple-950/20" : d.is_holiday ? "bg-amber-50/50 dark:bg-amber-950/20" : ""}`}>
                    <td className="p-2 font-mono text-[11px]">{fmtDatePR(d.date)}</td>
                    <td className="p-2">{d.weekday}</td>
                    <td className="p-2"><span className="text-[11px]">{d.status}</span>{d.reason && <span className="text-[10px] text-slate-500 italic ml-1">({d.reason})</span>}</td>
                    <td className="p-2 font-mono text-[10.5px]">{d.punch_in ? String(d.punch_in).slice(11, 16) : "—"}</td>
                    <td className="p-2 font-mono text-[10.5px]">{d.punch_out ? String(d.punch_out).slice(11, 16) : (d.punch_in ? <span className="text-rose-600">Not marked</span> : "—")}</td>
                    <td className="p-2 text-right font-mono">{d.hours_worked}</td>
                    <td className="p-2 text-right font-mono">{d.weight}</td>
                    <td className="p-2 text-right font-mono font-bold text-emerald-700 dark:text-emerald-400">{INR(d.day_pay)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* v109.9.137 — Locked salaries list for this month */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3" data-testid="sc-locked-list">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <div className="text-[13px] font-extrabold text-slate-800 dark:text-slate-100">
                🔒 Locked Salaries · {MONTHS[month - 1]} {year}
              </div>
              <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 border border-slate-300">
                {lockedList.count || 0} locked
              </Badge>
              <Button
                size="sm" variant="outline" className="ml-auto"
                onClick={async () => {
                  try {
                    const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                    const token = localStorage.getItem("token") || "";
                    const res = await fetch(`${API_URL}/api/payroll/salary-calc/locked/pdf?year=${year}&month=${month}`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!res.ok) { toast.error("PDF download failed"); return; }
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = `locked_salaries_${year}-${String(month).padStart(2, "0")}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                  } catch (e) { toast.error("Could not download PDF"); }
                }}
                disabled={(lockedList.count || 0) === 0}
                data-testid="sc-download-pdf-btn"
              >
                <FileText className="w-4 h-4 mr-1.5" /> Download PDF
              </Button>
            </div>
            {(lockedList.count || 0) === 0 ? (
              <div className="text-[12px] text-slate-500 italic py-3 text-center">
                No salaries locked yet for this month.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-600">
                    <tr>
                      <th className="text-left p-2">Employee Name</th>
                      <th className="text-left p-2">Employee Code</th>
                      <th className="text-right p-2">Calculated Salary</th>
                      <th className="text-left p-2">Account No.</th>
                      <th className="text-left p-2">IFSC Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(lockedList.items || []).map((it) => (
                      <tr key={it.employee_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`sc-locked-row-${it.employee_id}`}>
                        <td className="p-2 font-semibold">{it.employee_name}</td>
                        <td className="p-2 font-mono text-[11px]">{it.employee_code || "—"}</td>
                        <td className="p-2 text-right font-mono font-bold text-emerald-700 dark:text-emerald-400">{INR(it.calculated_salary)}</td>
                        <td className="p-2 font-mono text-[11px]">{it.account_no || "—"}</td>
                        <td className="p-2 font-mono text-[11px]">{it.ifsc || "—"}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-slate-300 dark:border-slate-600 bg-amber-50 dark:bg-amber-950/20 font-extrabold">
                      <td className="p-2" colSpan={2}>TOTAL</td>
                      <td className="p-2 text-right font-mono text-emerald-800 dark:text-emerald-300" data-testid="sc-locked-total">{INR(lockedList.grand_total || 0)}</td>
                      <td colSpan={2} />
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* v109.9.150 — Lock confirmation dialog: shows the data before locking */}
      <Dialog open={lockConfirm.open} onOpenChange={(v) => !v && setLockConfirm({ open: false, total: 0, rows: [] })}>
        <DialogContent className="max-w-2xl" data-testid="sc-lock-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Review & Lock Salary — {MONTHS[month - 1]} {year}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[13px]">
            <div className="rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[11px] uppercase font-bold text-slate-600 dark:text-slate-300">Calculated Monthly Salary</div>
                <div className="text-[22px] font-extrabold text-emerald-700 dark:text-emerald-300 font-mono" data-testid="sc-lock-confirm-total">{INR(lockConfirm.total)}</div>
              </div>
              <div className="text-[10.5px] text-slate-500">DS · {INR(calc?.per_day_rate || 0)} · {lockConfirm.rows?.length || 0} days · Non-negative formula</div>
            </div>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
              <table className="w-full text-[11.5px]">
                <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 sticky top-0">
                  <tr>
                    <th className="text-left p-1.5">Date</th>
                    <th className="text-left p-1.5">Code</th>
                    <th className="text-right p-1.5">Paid</th>
                    <th className="text-right p-1.5">Deducted</th>
                    <th className="text-right p-1.5">Day Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {(lockConfirm.rows || []).map((r) => {
                    const _paid = new Set(["P", "WO", "PH", "CL", "SL", "EL", "HD-CL", "HD-SL", "HD-EL"]);
                    const _ded = new Set(["A", "LWP", "HD-A", "HD-LWP"]);
                    const ds = Number(calc?.per_day_rate || 0);
                    const dp = _paid.has(r.status_code) ? r.paid_amount
                             : _ded.has(r.status_code) ? Math.max(0, ds - r.deducted_amount)
                             : 0;
                    return (
                      <tr key={r.date} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="p-1.5 font-mono">{fmtDatePR(r.date)}</td>
                        <td className="p-1.5 font-mono">{r.status_code || "-"}</td>
                        <td className="p-1.5 text-right font-mono">{INR(r.paid_amount)}</td>
                        <td className="p-1.5 text-right font-mono">{INR(r.deducted_amount)}</td>
                        <td className="p-1.5 text-right font-mono font-bold text-emerald-700 dark:text-emerald-300">{INR(dp)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="text-[11px] text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/25 rounded-md border border-amber-300 dark:border-amber-800 p-2">
              Once locked, only Master Admin can unlock. Ensure attendance changes and per-day paid/deducted amounts are correct.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setLockConfirm({ open: false, total: 0, rows: [] })} disabled={lockBusy} data-testid="sc-lock-confirm-cancel">
              Cancel
            </Button>
            <Button
              onClick={async () => {
                setLockBusy(true);
                try {
                  await api.post("/payroll/salary-calc/lock", {
                    employee_id: empId, year, month,
                    per_day: lockConfirm.rows,
                    calculated_salary: lockConfirm.total,
                  });
                  toast.success(`Salary locked · ${INR(lockConfirm.total)}`);
                  setLockConfirm({ open: false, total: 0, rows: [] });
                  await load();
                } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
                finally { setLockBusy(false); }
              }}
              disabled={lockBusy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="sc-lock-confirm-submit"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> {lockBusy ? "Locking…" : "Confirm & Lock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// -------------------------------------------------------------
// Attendance Cube Grid (Phase 2 UI)
// -------------------------------------------------------------
export function AttendanceGridTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [empId, setEmpId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [calc, setCalc] = useState(null);
  const [period, setPeriod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editDay, setEditDay] = useState(null);   // day object being edited
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [compOffTarget, setCompOffTarget] = useState(null);
  const [reportView, setReportView] = useState(null); // day whose report to show
  // v109.9.130 — Compact pending-attendance strip. Shows which employees
  // still owe attendance for previous months of the current year. Hidden
  // once every employee is finalised.
  const [pendingInfo, setPendingInfo] = useState({ employees: [], loading: true });
  const [pendingExpanded, setPendingExpanded] = useState(false);

  const loadPending = useCallback(async () => {
    try {
      const { data } = await api.get("/payroll/attendance/pending-employees");
      setPendingInfo({ employees: data?.employees || [], year: data?.year, prev_months: data?.prev_months || [], loading: false });
    } catch {
      setPendingInfo({ employees: [], loading: false });
    }
  }, []);
  useEffect(() => { loadPending(); }, [loadPending]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/employees");
        const list = (data || []).filter((e) => e.id).map((e) => ({
          id: e.id, name: e.name,
          tag: e.is_active === false ? "inactive" : (e.termination_date ? "terminated" : (e.resignation_date ? "resigned" : "active")),
        }));
        setEmployees(list);
        if (list.length && !empId) setEmpId(list[0].id);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    if (!empId) return;
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        api.get(`/payroll/salary-calc?employee_id=${empId}&year=${year}&month=${month}`),
        api.get(`/payroll/attendance/period?employee_id=${empId}&year=${year}&month=${month}`),
      ]);
      setCalc(c.data);
      setPeriod(p.data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (empId) load(); /* eslint-disable-next-line */ }, [empId, year, month]);

  // v109.9.129 — Publish is allowed only for PREVIOUS months (never for the
  // current or a future month). Finalise for Salary is gated behind the
  // employee's own "Calculate My Salary" (state == employee_submitted).
  const nowIst = new Date();
  const nowYear = nowIst.getUTCFullYear();
  const nowMonth = nowIst.getUTCMonth() + 1;
  const isPreviousMonth = (year < nowYear) || (year === nowYear && month < nowMonth);
  const publish = async () => {
    if (!isPreviousMonth) {
      toast.error("Publish is allowed only for previous months.");
      return;
    }
    try {
      await api.post("/payroll/attendance/publish", { employee_id: empId, year, month });
      toast.success("Published to employee for review");
      load();
      loadPending();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const pendingCompOff = (calc?.per_day || []).filter((r) => r.attendance_id && (r.comp_off_accrual || 0) > 0 && !["approved", "rejected"].includes(r.comp_off_status));
  const finalise = async () => {
    if (period?.state !== "employee_submitted") {
      toast.error("Employee must click Calculate My Salary first (state must be employee_submitted).");
      return;
    }
    if (!window.confirm("Finalise this month for salary calculation? Only Master Admin can edit after this.")) return;
    try {
      await api.post("/payroll/attendance/finalise", { employee_id: empId, year, month });
      toast.success("Month finalised for Salary Calc");
      load();
      loadPending();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // v109.9.148 — Master-Admin-only override: skip employee's Calculate-My-Salary click.
  const { user: currentUser } = useAuth();
  const isMasterAdmin = currentUser?.role === "master_admin";
  const forceCalculate = async () => {
    const reason = window.prompt(
      "Reason for Force Calculate (visible to the employee):",
      "Employee unavailable — proceeding on their behalf.",
    );
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) toast.error("Reason required (min 3 chars)");
      return;
    }
    try {
      await api.post("/payroll/attendance/force-calculate", {
        employee_id: empId, year, month, reason: reason.trim(),
      });
      toast.success("Force-calculated — attendance moved to employee_submitted");
      load();
      loadPending();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // v109.9.148 — Bulk-approve every pending edit-request for the visible month.
  const approveMonth = async () => {
    const pendingCount = (period?.edit_requests || []).filter((r) => r.status === "pending").length;
    if (!pendingCount) {
      toast.info("No pending edit-requests to approve.");
      return;
    }
    if (!window.confirm(`Approve all ${pendingCount} pending edit-request(s) for this month?`)) return;
    try {
      const { data } = await api.post("/payroll/attendance/approve-month", {
        employee_id: empId, year, month,
        remarks: "Approve Month",
      });
      toast.success(`Approved ${data?.approved_count ?? pendingCount} edit-request(s)`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const employee = employees.find((e) => e.id === empId);
  const c = calc?.counts || {};

  return (
    <div className="space-y-4" data-testid="attendance-grid-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <CalendarCheck className="w-5 h-5 text-indigo-600" />
        <h3 className="text-[15px] font-extrabold">Attendance</h3>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Select value={empId} onValueChange={setEmpId}>
            <SelectTrigger className="w-56" data-testid="attendance-grid-emp"><SelectValue placeholder="Pick employee" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name}{" "}
                  <Badge className={
                    e.tag === "active" ? "bg-emerald-100 text-emerald-800" :
                    e.tag === "inactive" ? "bg-slate-100 text-slate-700" :
                    e.tag === "terminated" ? "bg-rose-100 text-rose-800" :
                    "bg-amber-100 text-amber-800"} data-testid={`emp-tag-${e.id}`}>{e.tag}</Badge>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24" />
        </div>
      </div>

      {/* v109.9.130 — Compact pending-attendance strip. Chip strip, one row.
          Click any chip to open that employee-month directly. Hidden entirely
          when every employee is finalised for every previous month. */}
      {!pendingInfo.loading && pendingInfo.employees.length > 0 && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/25 px-2.5 py-1.5" data-testid="attendance-pending-strip">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wide text-amber-900 dark:text-amber-200 shrink-0">
              <AlertTriangle size={12} strokeWidth={2.5} /> Pending {pendingInfo.year}:
            </span>
            {(pendingExpanded ? pendingInfo.employees : pendingInfo.employees.slice(0, 8)).map((emp) => (
              <div key={emp.employee_id} className="inline-flex items-center gap-0.5" data-testid={`pending-emp-${emp.employee_id}`}>
                <button
                  className="text-[11px] font-semibold text-amber-900 dark:text-amber-100 hover:underline max-w-[110px] truncate"
                  title={emp.employee_name}
                  onClick={() => { setEmpId(emp.employee_id); setYear(pendingInfo.year); setMonth(emp.pending_months[0].month); }}
                >
                  {emp.employee_name}
                </button>
                {emp.pending_months.map((pm) => {
                  const isPublished = pm.state !== "not_published";
                  const tint = isPublished
                    ? "bg-sky-500 text-white"          // published but not finalised
                    : "bg-rose-500 text-white";       // not published at all
                  return (
                    <button
                      key={pm.month}
                      onClick={() => { setEmpId(emp.employee_id); setYear(pendingInfo.year); setMonth(pm.month); }}
                      className={`inline-block ${tint} rounded font-mono font-bold text-[9.5px] px-1 py-0.5 hover:opacity-80`}
                      title={`${MONTHS[pm.month-1]} · ${pm.state}`}
                      data-testid={`pending-chip-${emp.employee_id}-${pm.month}`}
                    >
                      {MONTHS[pm.month-1].slice(0, 3)}
                    </button>
                  );
                })}
              </div>
            ))}
            {pendingInfo.employees.length > 8 && (
              <button
                className="ml-1 text-[10.5px] font-semibold text-amber-900 dark:text-amber-200 underline"
                onClick={() => setPendingExpanded((v) => !v)}
                data-testid="pending-strip-toggle"
              >
                {pendingExpanded ? "− Collapse" : `+ ${pendingInfo.employees.length - 8} more`}
              </button>
            )}
            <span className="ml-auto inline-flex items-center gap-2 text-[9.5px] text-amber-900/70 dark:text-amber-300/70 shrink-0">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded bg-rose-500 inline-block" /> Not Published</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded bg-sky-500 inline-block" /> Not Finalised</span>
            </span>
          </div>
        </div>
      )}

      {/* Period state + publish/finalise */}
      {period && (
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={period.state === "finalised" ? "bg-emerald-100 text-emerald-800" : period.state === "published" ? "bg-sky-100 text-sky-800" : period.state === "employee_submitted" ? "bg-indigo-100 text-indigo-800" : period.state === "employee_edits_requested" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"} data-testid="attendance-period-state">
            State: {period.state}
          </Badge>
          {period.state === "finalised" && (
            <>
              <Badge className="bg-rose-100 text-rose-800 border border-rose-300" data-testid="attendance-locked-badge">Locked — no edits allowed</Badge>
              <Button size="sm" variant="outline" onClick={() => setUnlockOpen(true)} className="h-7 text-[10.5px] border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="attendance-unlock-btn">
                Change State (Password)
              </Button>
            </>
          )}
          {(period.edit_requests || []).filter((r) => r.status === "pending").length > 0 && (
            <Badge className="bg-amber-100 text-amber-800">
              {period.edit_requests.filter((r) => r.status === "pending").length} pending edit-requests
            </Badge>
          )}
          {/* v109.9.136 — Recheck: re-derive statuses from latest punches + leaves */}
          <Button size="sm" variant="outline" onClick={() => load()} className="h-7 text-[10.5px]" data-testid="attendance-recheck-btn" title="Recompute status codes from punches + approved leaves">
            <CalendarCheck className="w-3.5 h-3.5 mr-1" /> Recheck Status
          </Button>
          {period.state !== "finalised" && (
            <>
              {/* v109.9.129 — Publish only for previous months, once. */}
              {isPreviousMonth && !["published", "employee_edits_requested", "employee_submitted"].includes(period.state) && (
                <Button size="sm" onClick={publish} className="bg-sky-600 hover:bg-sky-700" data-testid="attendance-publish-btn">
                  <Save className="w-3.5 h-3.5 mr-1" /> Publish to Employee
                </Button>
              )}
              {!isPreviousMonth && !["published", "employee_edits_requested", "employee_submitted"].includes(period.state) && (
                <Badge className="bg-slate-100 text-slate-500 text-[10.5px]" data-testid="attendance-publish-blocked">
                  Publish available only for previous months
                </Badge>
              )}
              {/* v109.9.148 — Approve Month button (visible whenever any edit-request is pending). */}
              {(period.edit_requests || []).filter((r) => r.status === "pending").length > 0 && (
                <Button
                  size="sm"
                  onClick={approveMonth}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="attendance-approve-month-btn"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve Month ({(period.edit_requests || []).filter((r) => r.status === "pending").length})
                </Button>
              )}
              {/* v109.9.148 — Master-Admin-only Force Calculate bypasses the employee click. */}
              {isMasterAdmin && ["published", "employee_edits_requested"].includes(period.state) && (
                <Button
                  size="sm"
                  onClick={forceCalculate}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="attendance-force-calc-btn"
                  title="Bypass the employee's Calculate My Salary click"
                >
                  <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Force Calculate
                </Button>
              )}
              {/* v109.9.129 — Finalise for Salary appears only after the
                  employee has clicked "Calculate My Salary". */}
              {period.state === "employee_submitted" && pendingCompOff.length === 0 && (
                <Button size="sm" onClick={finalise} className="bg-emerald-600 hover:bg-emerald-700" data-testid="attendance-finalise-btn">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Finalize for Salary
                </Button>
              )}
              {pendingCompOff.length > 0 && (
                <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10.5px] gap-1 flex-wrap" data-testid="attendance-compoff-pending-badge">
                  {pendingCompOff.length} Comp-Off pending — approve or disapprove before Finalize:
                  {pendingCompOff.map((r) => (
                    <button key={r.date} type="button" className="underline font-bold hover:text-amber-950"
                      onClick={() => document.querySelector(`[data-testid="att-cube-${r.date}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" })}
                      data-testid={`attendance-compoff-jump-${r.date}`}>
                      {r.date.slice(8)}/{r.date.slice(5, 7)}
                    </button>
                  ))}
                </Badge>
              )}
              {period.state === "published" && (
                <Badge className="bg-sky-50 text-sky-700 border border-sky-200 text-[10.5px]" data-testid="attendance-awaiting-employee">
                  Awaiting employee to click Calculate My Salary
                </Badge>
              )}
              {/* v109.9.148 — Auto-submitted or Force-calculated banners */}
              {period.auto_submitted && (
                <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10.5px]" data-testid="attendance-auto-submitted-badge" title={period.auto_submitted_reason || ""}>
                  Auto-submitted (past 5th)
                </Badge>
              )}
              {period.force_calculated && (
                <Badge className="bg-purple-100 text-purple-800 border border-purple-300 text-[10.5px]" data-testid="attendance-force-calc-badge" title={period.force_calculated_reason || ""}>
                  Force-calculated by {period.force_calculated_by_name || "Master Admin"}
                </Badge>
              )}
            </>
          )}
        </div>
      )}

      {loading && <div className="p-6 text-center text-slate-400">Loading…</div>}

      {!loading && calc && (
        <>
          {/* Attendance tiles — linear big-card grid, NOT calendar. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="attendance-tile-grid">
            {(() => {
              const visible = calc.per_day.filter(isPastOrToday);
              // Safety-net: if the filter yields zero cubes (e.g., viewing a
              // future month where nothing has "happened yet") fall back to
              // showing the full month so the grid never appears empty by
              // accident. This is the fix for v109.9.42 — some browsers were
              // returning zero-padded-less ISO strings from `toLocaleDateString`
              // which made the string comparison filter out every day.
              return visible.length > 0 ? visible : calc.per_day;
            })().map((d) => (
              <AttendanceCube
                key={d.date} d={d}
                locked={period?.state === "finalised"}
                onCompOff={(decision) => setCompOffTarget({ day: d, decision })}
                pendingEdits={(period?.edit_requests || []).filter((r) => r.date === d.date && r.status === "pending")}
                onEdit={() => setEditDay(d)}
                onOpenReport={() => setReportView(d)}
                onReviewEdit={async (er, decision, remarks) => {
                  try {
                    await api.post("/payroll/attendance/edit-request/review", { employee_id: empId, year, month, date: er.date, decision, remarks: remarks || "" });
                    toast.success(decision === "approved" ? "Approved" : "Rejected");
                    load();
                  } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
                }}
              />
            ))}
          </div>

          {/* Stat card */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="attendance-stat-card">
            <StatBox label="Working Days" value={calc.working_days} />
            <StatBox label="Present" value={calc.counts?.present ?? 0} tone="emerald" />
            <StatBox label="Absent" value={calc.counts?.absent ?? 0} tone="rose" />
            <StatBox label="Paid Leave" value={calc.counts?.paid_leave ?? 0} tone="sky" />
            <StatBox label="Half Days" value={calc.counts?.half ?? 0} tone="amber" />
            <StatBox label="Holiday Worked" value={calc.counts?.holiday_worked ?? 0} tone="purple" />
            <StatBox label="Reports Missing" value={calc.counts?.reports_missing ?? 0} tone="rose" />
            <StatBox label="Punch-outs Missing" value={calc.counts?.punchouts_missing ?? 0} tone="rose" />
            <StatBox label="Late Punch-ins (after cutoff)" value={calc.counts?.late_punchins ?? 0} tone="amber" />
            <StatBox label="Total Hrs (Working)" value={`${(calc.working_days * 8.5).toFixed(1)}h expected`} tone="slate" />
            <StatBox label="Edits Last Month" value={calc.counts?.edits_last_month ?? 0} tone="sky" />
            <StatBox label="Total Edits (till this month)" value={calc.counts?.edits_total_till_month ?? 0} tone="sky" />
          </div>

          {/* Pending edit requests */}
          {period?.state !== "finalised" && (period?.edit_requests || []).filter((r) => r.status === "pending").length > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3">
              <div className="text-[13px] font-extrabold text-amber-800 dark:text-amber-200 mb-2">Pending Edit Requests</div>
              <div className="space-y-1.5">
                {period.edit_requests.filter((r) => r.status === "pending").map((er, i) => (
                  <div key={i} className="rounded-md border border-amber-200 bg-white dark:bg-slate-900 p-2 flex items-center gap-2 text-[12px]">
                    <span className="font-mono">{fmtDatePR(er.date)}</span>
                    <span className="flex-1 text-slate-700 dark:text-slate-200">{er.reason}</span>
                    <Button size="sm" className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-700"
                      onClick={async () => {
                        try { await api.post("/payroll/attendance/edit-request/review", { employee_id: empId, year, month, date: er.date, decision: "approved", remarks: "" }); toast.success("Approved"); load(); }
                        catch (e) { toast.error(formatError(e.response?.data?.detail)); }
                      }}
                      data-testid={`att-edit-approve-${er.date}`}
                    >Approve</Button>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] text-rose-700"
                      onClick={async () => {
                        const remarks = window.prompt("Rejection remarks:", "");
                        if (remarks === null) return;
                        try { await api.post("/payroll/attendance/edit-request/review", { employee_id: empId, year, month, date: er.date, decision: "rejected", remarks }); toast.success("Rejected"); load(); }
                        catch (e) { toast.error(formatError(e.response?.data?.detail)); }
                      }}
                      data-testid={`att-edit-reject-${er.date}`}
                    >Reject</Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {!loading && !calc && empId && <div className="p-6 text-center text-slate-400">No data.</div>}

      {/* Admin edit dialog (pencil-icon on each cube) */}
      <AttendanceDayEditDialog
        open={!!editDay} onClose={() => setEditDay(null)}
        day={editDay} employeeId={empId} onSaved={load}
      />

      <CompOffDecideDialog target={compOffTarget} onClose={() => setCompOffTarget(null)} onDone={load} />
      <UnlockMonthDialog
        open={unlockOpen} onClose={() => setUnlockOpen(false)}
        employeeId={empId} year={year} month={month} onUnlocked={load}
      />

      {/* Report-view banner (opened when admin clicks "Submitted @ …") */}
      <AttendanceReportBanner
        open={!!reportView} onClose={() => setReportView(null)} day={reportView}
      />
    </div>
  );
}

// -------------------------------------------------------------
// Attendance cube tile — larger card OUT OF calendar layout.
// -------------------------------------------------------------
// -------------------------------------------------------------
// Orange-dot popover — surfaces a single field's pending edit-request
// to the admin with Accept / Reject buttons + orig/edited/remark.
// -------------------------------------------------------------
function EditReviewDot({ er, origLabel, origValue, editedLabel, editedValue, onReview, testid }) {
  const [rejReason, setRejReason] = useState("");
  if (!er) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center justify-center w-3 h-3 rounded-full bg-orange-500 ring-2 ring-orange-200 hover:bg-orange-600 animate-pulse"
          title="Pending edit-request — click to review"
          data-testid={testid}
        />
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3 z-50" data-testid={`${testid}-popover`}>
        <div className="space-y-2 text-[12px]">
          <div className="font-bold text-orange-700 uppercase text-[10px]">Employee Edit Request</div>
          <div className="rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-1.5">
            <div className="text-[9px] uppercase font-bold text-slate-500">{origLabel || "Original"}</div>
            <div className="font-mono">{String(origValue ?? "—")}</div>
          </div>
          <div className="rounded border border-orange-200 bg-orange-50 dark:bg-orange-950/20 p-1.5">
            <div className="text-[9px] uppercase font-bold text-orange-700">{editedLabel || "Proposed"}</div>
            <div className="font-mono text-orange-800 dark:text-orange-200">{String(editedValue ?? "—")}</div>
          </div>
          <div className="rounded border border-amber-200 bg-amber-50 dark:bg-amber-950/20 p-1.5">
            <div className="text-[9px] uppercase font-bold text-amber-700">Remark</div>
            <div className="italic">{er.reason || "—"}</div>
          </div>
          <div className="flex flex-col gap-1.5 pt-1">
            <Button
              size="sm"
              className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]"
              onClick={() => onReview?.(er, "approved", "")}
              data-testid={`${testid}-approve`}
            >
              <CheckCircle2 className="w-3 h-3 mr-1" /> Accept
            </Button>
            <div className="flex items-center gap-1">
              <Input
                placeholder="Rejection reason"
                value={rejReason}
                onChange={(e) => setRejReason(e.target.value)}
                className="h-7 text-[11px]"
                data-testid={`${testid}-reject-reason`}
              />
              <Button
                size="sm"
                variant="outline"
                className="h-7 border-rose-300 text-rose-700 text-[11px]"
                onClick={() => onReview?.(er, "rejected", rejReason.trim() || "Not satisfactory")}
                data-testid={`${testid}-reject`}
              >
                <XCircle className="w-3 h-3 mr-1" /> Reject
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// -------------------------------------------------------------
// Attendance cube tile — larger card OUT OF calendar layout.
// -------------------------------------------------------------
/**
 * v109.9.132 — Prompts the admin to set an employee's Monthly In-Hand
 * Salary when the enrollment record has it blank. Saves back into the
 * user doc (same field Employee Enrollment reads).
 */
function MissingSalaryPanel({ employeeId, employeeName, onSaved }) {
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const n = Number(amount);
    if (!n || n <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      await api.post("/payroll/salary-calc/set-monthly-salary", {
        employee_id: employeeId,
        monthly_salary: n,
      });
      toast.success(`Monthly salary set for ${employeeName || "employee"}`);
      onSaved?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <div
      className="rounded-xl border-2 border-amber-300 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/25 p-4"
      data-testid="salary-calc-missing-salary"
    >
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle size={16} className="text-amber-600" strokeWidth={2.4} />
        <div className="text-[13.5px] font-bold text-amber-900 dark:text-amber-100">
          Monthly Salary not set for {employeeName || "this employee"}
        </div>
      </div>
      <div className="text-[12px] text-amber-800 dark:text-amber-200 mb-3">
        Add the Monthly In-Hand Salary below to unlock the calculation. This value will be saved
        to the Employee Enrollment record — the whole app will use it going forward.
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 font-bold text-[13px]">₹</span>
          <Input
            type="number"
            min="1"
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Monthly in-hand salary"
            className="pl-6 w-56"
            data-testid="salary-calc-missing-input"
          />
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="salary-calc-missing-save"
        >
          {saving ? "Saving…" : "Save & Continue"}
        </Button>
      </div>
    </div>
  );
}

/**
 * v109.9.131 — Read-only mini attendance tile for the Salary Calc tab.
 * Deduction-status tiles (A / LWP / HD-A / HD-LWP) are clickable to toggle
 * "pay this day" (`paid=true` reverses the deduction).
 */
function SalaryDayTile({ day, paid, onToggle }) {
  const flags = classifyStatus(day);
  const code = statusToCode(day.status, day);
  const clickable = day.is_deduction_candidate;
  const deducted = day.is_deducted && !paid;
  const base = statusCellClasses(flags, false);
  return (
    <button
      type="button"
      onClick={clickable ? onToggle : undefined}
      disabled={!clickable}
      className={`relative rounded-md border p-1.5 text-left transition ${base} ${clickable ? "hover:scale-[1.03] cursor-pointer" : "cursor-default"} ${deducted ? "ring-2 ring-rose-500 ring-offset-1" : ""} ${paid ? "ring-2 ring-emerald-500 ring-dashed ring-offset-1 opacity-90" : ""}`}
      title={
        !clickable
          ? `${fmtDatePR(day.date)} · ${day.status}`
          : paid
            ? "Click to restore deduction"
            : `Click to pay this day (currently −₹${Math.round(day.deduction_amount || 0)})`
      }
      data-testid={`salary-tile-${day.date}`}
    >
      <div className="text-[10px] font-mono text-slate-500">{day.date?.slice(8)}</div>
      <div className="text-[10.5px] font-bold leading-tight">{code}</div>
      {clickable && (
        <div className={`text-[9px] font-semibold ${paid ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"}`}>
          {paid ? "PAID ✓" : `− ₹${Math.round(day.deduction_amount || 0)}`}
        </div>
      )}
      {!clickable && day.hours_worked > 0 && (
        <div className="text-[9px] text-slate-500 font-mono">{Number(day.hours_worked).toFixed(1)}h</div>
      )}
    </button>
  );
}

function AttendanceCube({ d, pendingEdits: rawPending = [], onEdit: rawEdit, onOpenReport, onReviewEdit: rawReview, locked = false, onCompOff }) {
  const pendingEdits = locked ? [] : rawPending;
  const onEdit = locked ? null : rawEdit;
  const onReviewEdit = locked ? null : rawReview;  const flags = classifyStatus(d);
  const { isSunday, isSunWorked: sunPunched, isPureHoliday } = flags;
  const hasCompOff = (d.comp_off_accrual || 0) > 0 || ["approved", "rejected"].includes(d.comp_off_status);
  const isSunWorked = sunPunched || (isSunday && hasCompOff);
  const isHolidayWorked = d.status && (d.status.includes("Worked (") || d.status.includes("Worked · "));
  const { container: cls, chip: chipCls } = statusCellClasses(flags);

  // Helpers to find a pending edit by field.
  const pinPending = pendingEdits.find((e) => e.field === "punch_in");
  const poutPending = pendingEdits.find((e) => e.field === "punch_out");
  const rptPending = pendingEdits.find((e) => e.field === "report");
  const statusPending = pendingEdits.find((e) => e.field === "status");

  const fmtTime = fmt12h;
  const fmtDT = (v) => fmtDateTimePR(v, null);

  // Sunday tile with no activity — render a minimal weekly-holiday card.
  if (isSunday && !isSunWorked) {
    return (
      <div className={`rounded-xl border-2 p-4 ${cls}`} data-testid={`att-cube-${d.date}`}>
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="text-[20px] font-extrabold leading-none">{fmtDatePR(d.date)}</div>
            <div className="text-[13px] font-semibold text-slate-700 dark:text-slate-200 mt-0.5">{d.weekday}</div>
          </div>
          {onEdit && (
            <button onClick={onEdit} className="opacity-50 hover:opacity-100" title="Edit" data-testid={`att-cube-edit-${d.date}`}>
              <Pencil className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className={`inline-block px-2 py-1 rounded font-bold text-[11px] uppercase ${chipCls}`} title="Weekly Off — Paid">
          <span className="font-mono mr-1">WO</span>· Weekly Off · Paid
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border-2 p-4 ${cls}`} data-testid={`att-cube-${d.date}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-[20px] font-extrabold leading-none">{fmtDatePR(d.date)}</div>
          <div className="text-[13px] font-semibold text-slate-700 dark:text-slate-200 mt-0.5">{d.weekday}</div>
        </div>
        <div className="flex items-center gap-1.5">
          {/* v109.9.148 — Approve-Day: bulk-approve every pending edit for this card. */}
          {pendingEdits.length > 0 && (
            <button
              onClick={() => {
                if (!window.confirm(`Approve all ${pendingEdits.length} pending edit(s) for ${fmtDatePR(d.date)}?`)) return;
                pendingEdits.forEach((er) => onReviewEdit?.(er, "approved", "Approved via Approve Day"));
              }}
              className="inline-flex items-center gap-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-0.5 text-[10px] font-bold"
              title={`Approve all ${pendingEdits.length} pending edit(s) for this day`}
              data-testid={`att-cube-approve-day-${d.date}`}
            >
              <CheckCircle2 className="w-3 h-3" /> Approve Day ({pendingEdits.length})
            </button>
          )}
          {onEdit && (
            <button
              onClick={onEdit}
              className="opacity-60 hover:opacity-100 transition-opacity"
              title="Edit"
              data-testid={`att-cube-edit-${d.date}`}
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <MasterUpdateTag day={d} testid={`att-cube-master-update-${d.date}`} />
      {/* Status chip — CODE · Label */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className={`inline-block px-2 py-1 rounded font-bold text-[11px] uppercase ${chipCls}`} title={d.status}>
          <span className="font-mono mr-1 font-bold" data-testid={`att-cube-code-${d.date}`}>{statusToCode(d.status, d)}</span>
          <span className="text-slate-400 mx-0.5">|</span> {codeToLabel(statusToCode(d.status, d))}
        </div>
        <LeaveStateTag
          state={d.leave_state}
          emergency={d.leave_is_emergency}
          autoGenerated={d.leave_auto_generated}
          autoConvertedLwp={d.leave_auto_converted_to_lwp}
        />
        <EditReviewDot
          er={statusPending}
          origLabel="Current Status" origValue={d.status}
          editedLabel="Proposed Status" editedValue={statusPending?.proposed?.status}
          onReview={onReviewEdit}
          testid={`att-cube-dot-status-${d.date}`}
        />
      </div>

      {/* Sunday / holiday worked comp-off tag */}
      {(isSunWorked || hasCompOff) && (
        <div className="mb-2">
          {d.comp_off_status === "approved" ? (
            <div className="inline-flex items-center gap-1.5 rounded-md bg-purple-600 text-white px-2 py-1 text-[11px] font-bold">
              <CheckCircle2 className="w-3.5 h-3.5" /> Comp Off Approved · {d.comp_off_accrual || 0} day{(d.comp_off_accrual || 0) > 1 ? "s" : ""}
            </div>
          ) : d.comp_off_status === "rejected" ? (
            <div className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 text-white px-2 py-1 text-[11px] font-bold">
              <XCircle className="w-3.5 h-3.5" /> Comp Off Rejected
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 text-white px-2 py-1 text-[11px] font-bold">
              <Clock className="w-3.5 h-3.5" /> Pending Comp-Off Approval · {d.comp_off_accrual || 0} day
            </div>
          )}
          {d.comp_off_remarks && (
            <div className="text-[10.5px] text-slate-600 dark:text-slate-300 mt-1 italic" data-testid={`att-cube-compoff-remark-${d.date}`}>
              &ldquo;{d.comp_off_remarks}&rdquo;{d.comp_off_decided_by ? ` — ${d.comp_off_decided_by}` : ""}
            </div>
          )}
          {onCompOff && d.attendance_id && ["approved", "rejected"].includes(d.comp_off_status) && (
            <Button size="sm" variant="outline" className={`h-6 mt-1.5 text-[10px] ${d.comp_off_status === "approved" ? "text-rose-700 border-rose-300" : "text-emerald-700 border-emerald-300"}`}
              onClick={() => onCompOff(d.comp_off_status === "approved" ? "rejected" : "approved")}
              title="Allowed until this month's salary is locked"
              data-testid={`att-cube-compoff-change-${d.date}`}>
              Change → {d.comp_off_status === "approved" ? "Disapprove" : "Approve"}
            </Button>
          )}
          {onCompOff && d.attendance_id && !["approved", "rejected"].includes(d.comp_off_status) && (
            <div className="flex gap-1.5 mt-1.5">
              <Button size="sm" className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onCompOff("approved")} data-testid={`att-cube-compoff-approve-${d.date}`}>
                <CheckCircle2 className="w-3 h-3 mr-0.5" /> Approve
              </Button>
              <Button size="sm" variant="outline" className="h-6 text-[10px] text-rose-700 border-rose-300" onClick={() => onCompOff("rejected")} data-testid={`att-cube-compoff-reject-${d.date}`}>
                <XCircle className="w-3 h-3 mr-0.5" /> Disapprove
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Punch In / Out grid */}
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div className="rounded-md bg-white/60 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 p-2">
          <div className="text-[9px] uppercase font-bold text-slate-500 mb-0.5 flex items-center gap-1">
            Punch In
            <EditReviewDot
              er={pinPending}
              origLabel="Original" origValue={d.punch_in ? fmt12h(d.punch_in) : "Not marked"}
              editedLabel="Proposed" editedValue={pinPending?.proposed?.punch_in ? fmt12h(pinPending.proposed.punch_in) : "—"}
              onReview={onReviewEdit}
              testid={`att-cube-dot-in-${d.date}`}
            />
          </div>
          {d.punch_in ? (
            <div className="font-mono text-[13px] font-bold text-emerald-700 dark:text-emerald-400">{fmtTime(d.punch_in)}</div>
          ) : (
            <div className="font-bold text-[11px] text-rose-600">Not Marked</div>
          )}
        </div>
        <div className="rounded-md bg-white/60 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 p-2">
          <div className="text-[9px] uppercase font-bold text-slate-500 mb-0.5 flex items-center gap-1">
            Punch Out
            <EditReviewDot
              er={poutPending}
              origLabel="Original" origValue={d.punch_out ? fmt12h(d.punch_out) : "Not marked"}
              editedLabel="Proposed" editedValue={poutPending?.proposed?.punch_out ? fmt12h(poutPending.proposed.punch_out) : "—"}
              onReview={onReviewEdit}
              testid={`att-cube-dot-out-${d.date}`}
            />
          </div>
          {d.punch_out ? (
            <div className="font-mono text-[13px] font-bold text-emerald-700 dark:text-emerald-400">{fmtTime(d.punch_out)}</div>
          ) : (
            <div className="font-bold text-[11px] text-rose-600">Not Marked</div>
          )}
        </div>
      </div>

      {/* Working duration */}
      {d.hours_worked > 0 && (
        <div className="flex items-center gap-1.5 mb-2">
          <Clock className="w-3.5 h-3.5 text-emerald-700 dark:text-emerald-400" />
          <div className="font-mono text-[13px] font-bold text-emerald-700 dark:text-emerald-400">
            Worked {fmtHoursHM(d.hours_worked)}
          </div>
        </div>
      )}

      {/* Report submitted */}
      <div className="mb-2">
        <div className="text-[9px] uppercase font-bold text-slate-500 mb-0.5 flex items-center gap-1">
          Daily Report
          <EditReviewDot
            er={rptPending}
            origLabel="Original" origValue={d.report_content ? d.report_content.slice(0, 80) + (d.report_content.length > 80 ? "…" : "") : (d.report_submitted ? "Submitted" : "Not filled")}
            editedLabel="Proposed" editedValue={rptPending?.proposed?.report ? String(rptPending.proposed.report).slice(0, 80) + (String(rptPending.proposed.report).length > 80 ? "…" : "") : "—"}
            onReview={onReviewEdit}
            testid={`att-cube-dot-rpt-${d.date}`}
          />
        </div>
        {d.report_submitted ? (
          <button
            onClick={onOpenReport}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 text-[11px] font-bold transition-colors"
            data-testid={`att-cube-report-${d.date}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Submitted{d.report_submitted_at ? ` @ ${fmtDT(d.report_submitted_at)}` : ""} · view
          </button>
        ) : (
          <div className="inline-flex items-center gap-1.5 rounded-md bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 px-2 py-1 text-[11px] font-bold border border-rose-300">
            <XCircle className="w-3.5 h-3.5" /> Not Filled
          </div>
        )}
      </div>

      {/* Leave tag */}
      {d.leave_type && (
        <div className="mb-1">
          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${d.leave_is_unpaid ? "bg-orange-600 text-white" : d.leave_is_half_day ? "bg-yellow-500 text-black" : "bg-slate-500 text-white"}`}>
            {d.leave_type}{d.leave_is_half_day ? " · ½" : ""}{d.leave_is_unpaid ? " · unpaid" : ""}
          </span>
        </div>
      )}

      {/* Reason */}
      {d.reason && <div className="text-[11px] text-slate-500 dark:text-slate-400 italic mt-1" title={d.reason}>{d.reason}</div>}
    </div>
  );
}

// Report-view banner dialog — opens when admin clicks a "Submitted" button.
// v109.9.127 — Rich rendering of the actual Daily Report (tasks / challenges
// / next-day plan) + a link back to the Daily Report module.
function AttendanceReportBanner({ open, onClose, day }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="att-report-banner">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            Daily Report — {fmtDatePR(day?.date)} · {day?.weekday}
            {day?.report_is_late && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-amber-500 text-white" data-testid="att-report-banner-late">
                Late Report
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="text-[12px] text-slate-500 mb-3">
          {(day?.report_submitted_at_ist || day?.report_submitted_at) && (
            <>Submitted on <strong>{
              day?.report_submitted_at_ist
                || fmtDateTimePR(day.report_submitted_at)
            }</strong></>
          )}
        </div>

        {day?.report_id ? (
          <div className="space-y-3">
            {day?.report_tasks_completed && (
              <ReportBlock tint="emerald" label="Tasks Completed" text={day.report_tasks_completed} />
            )}
            {day?.report_challenges && (
              <ReportBlock tint="amber" label="Challenges" text={day.report_challenges} />
            )}
            {day?.report_next_day_plan && (
              <ReportBlock tint="sky" label="Plan for Tomorrow" text={day.report_next_day_plan} />
            )}
            {!day?.report_tasks_completed && !day?.report_challenges && !day?.report_next_day_plan && (
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-4 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-800 dark:text-slate-100 min-h-[120px]" data-testid="att-report-banner-body">
                {day?.report_content || <span className="text-slate-400 italic">No content stored for this report.</span>}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-4 text-[13px] text-slate-500 italic">
            No daily report found for this date.
          </div>
        )}

        <DialogFooter className="mt-3 gap-2">
          {day?.report_id && (
            <Button
              variant="outline"
              onClick={() => window.open("/module/hr_daily_report", "_blank")}
              className="text-[12.5px]"
              data-testid="att-report-banner-open-module"
            >
              Open in Daily Report Module ↗
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportBlock({ tint, label, text }) {
  const tints = {
    emerald: "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800/60 text-emerald-800 dark:text-emerald-200",
    amber:   "border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/60 text-amber-800 dark:text-amber-200",
    sky:     "border-sky-300 bg-sky-50 dark:bg-sky-950/30 dark:border-sky-800/60 text-sky-800 dark:text-sky-200",
  };
  return (
    <div className={`rounded-lg border-l-4 border ${tints[tint]} p-3`}>
      <div className="text-[10.5px] uppercase tracking-widest font-bold opacity-80 mb-1">{label}</div>
      <div className="text-[13px] leading-relaxed whitespace-pre-wrap text-slate-800 dark:text-slate-100">{text}</div>
    </div>
  );
}

// Admin edit dialog for a specific day.
function AttendanceDayEditDialog({ open, onClose, day, employeeId, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!day) return;
    // v109.9.136 — Prefer canonical status_code if present.
    const seed = day.status_code || (day.status && !day.status.startsWith("Absent (no record)") ? day.status : "P");
    setForm({
      status: seed,
      punch_in: day.punch_in ? String(day.punch_in).slice(11, 16) : "",
      punch_out: day.punch_out ? String(day.punch_out).slice(11, 16) : "",
      report_submitted: !!day.report_submitted,
      status_reason: day.reason || "",
    });
  }, [day]);

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.post("/payroll/attendance/day-edit", {
        employee_id: employeeId,
        date: day.date,
        ...form,
      });
      if (res?.data?.leave_auto_created) {
        if (res.data.auto_converted_to_lwp) {
          toast.warning(res.data.conversion_note || `Leave auto-created & converted to ${res.data.kind_label} (insufficient balance).`, { duration: 8000 });
        } else {
          toast.success(`Auto-generated ${res.data.kind_label} leave sent to Master Admin for approval.`, { duration: 6000 });
        }
      } else {
        toast.success("Attendance updated");
      }
      onSaved?.();
      onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="att-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit — {day?.weekday}, {fmtDatePR(day?.date)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <div>
            <Label>Status</Label>
            <Select value={form.status || "Present"} onValueChange={(v) => setForm((f) => {
              const dft = { P: ["09:30", "18:30"], "HD-A": ["09:30", "13:30"] }[v];
              if (dft) return { ...f, status: v, punch_in: f.punch_in || dft[0], punch_out: f.punch_out || dft[1] };
              return { ...f, status: v };
            })}>
              <SelectTrigger data-testid="att-edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {/* v109.9.136 — Canonical status set only. */}
                {/* Direct statuses (set immediately, no leave request) */}
                <SelectItem value="P">P — Present</SelectItem>
                <SelectItem value="A">A — Absent</SelectItem>
                <SelectItem value="HD-A">HD-A — Half Day Present</SelectItem>
                <SelectItem value="WO">WO — Weekly Off</SelectItem>
                <SelectItem value="PH">PH — Public Holiday</SelectItem>
                {/* Leave codes → auto-create a leave request to Master Admin */}
                <SelectItem value="CL">CL — Casual Leave (auto-generates leave req)</SelectItem>
                <SelectItem value="SL">SL — Sick Leave (auto-generates leave req)</SelectItem>
                <SelectItem value="EL">EL — Earned Leave (auto-generates leave req)</SelectItem>
                <SelectItem value="LWP">LWP — Leave Without Pay (auto-generates)</SelectItem>
                <SelectItem value="HD-CL">HD-CL — Half Day Casual Leave (auto-gen)</SelectItem>
                <SelectItem value="HD-SL">HD-SL — Half Day Sick Leave (auto-gen)</SelectItem>
                <SelectItem value="HD-EL">HD-EL — Half Day Earned Leave (auto-gen)</SelectItem>
                <SelectItem value="HD-LWP">HD-LWP — Half Day LWP (auto-gen)</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-[10px] text-slate-500 mt-1 leading-snug">
              Picking any Leave code (CL/SL/EL/LWP or HD-*) auto-creates a Leave Request routed to Master Admin. Balance is checked — insufficient balance auto-converts to LWP.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Punch In</Label>
              <AmPmTimeInput
                value={form.punch_in || ""}
                onChange={(v) => setForm({ ...form, punch_in: v })}
                testid="att-edit-in"
              />
            </div>
            <div>
              <Label>Punch Out</Label>
              <AmPmTimeInput
                value={form.punch_out || ""}
                onChange={(v) => setForm({ ...form, punch_out: v })}
                testid="att-edit-out"
              />
            </div>
          </div>
          {/* Live-computed working hours */}
          {form.punch_in && form.punch_out && (
            <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-300 dark:border-emerald-700 px-3 py-1.5 flex items-center gap-1.5 text-[12px] font-mono font-bold text-emerald-800 dark:text-emerald-200" data-testid="att-edit-hours">
              <Clock className="w-3.5 h-3.5" />
              Working Hours: {fmtHoursHM(computeHoursWorked(form.punch_in, form.punch_out))}
              <span className="opacity-60 font-normal ml-1">({fmt12h(form.punch_in)} → {fmt12h(form.punch_out)})</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={!!form.report_submitted} onChange={(e) => setForm({ ...form, report_submitted: e.target.checked })} data-testid="att-edit-rpt" />
            <Label>Report Submitted</Label>
          </div>
          <div>
            <Label>Reason / Note</Label>
            <Input value={form.status_reason || ""} onChange={(e) => setForm({ ...form, status_reason: e.target.value })} data-testid="att-edit-reason"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="att-edit-save">
            <Save className="w-4 h-4 mr-1.5" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatBox({ label, value, tone = "slate" }) {
  const cls = {
    slate: "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100",
    emerald: "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 text-emerald-900 dark:text-emerald-100",
    rose: "bg-rose-50 dark:bg-rose-950/20 border-rose-200 text-rose-900 dark:text-rose-100",
    sky: "bg-sky-50 dark:bg-sky-950/20 border-sky-200 text-sky-900 dark:text-sky-100",
    amber: "bg-amber-50 dark:bg-amber-950/20 border-amber-200 text-amber-900 dark:text-amber-100",
    purple: "bg-purple-50 dark:bg-purple-950/20 border-purple-200 text-purple-900 dark:text-purple-100",
  }[tone];
  return (
    <div className={`rounded-lg border p-2.5 ${cls}`}>
      <div className="text-[9.5px] uppercase font-bold opacity-80">{label}</div>
      <div className="text-[16px] font-extrabold mt-0.5">{value}</div>
    </div>
  );
}


// -------------------------------------------------------------
// Salary Advance Dashboard button — quick-request out of module
// -------------------------------------------------------------
export function SalaryAdvanceDashboardButton() {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/salary/advances/mine");
      setHistory(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (open) loadHistory(); }, [open]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      await api.post("/salary/advances", { amount: amt, reason: reason.trim() });
      toast.success("Advance requested");
      setAmount(""); setReason("");
      loadHistory();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        className="border-sky-300 text-sky-800 hover:bg-sky-50"
        data-testid="dashboard-advance-btn"
      >
        <Wallet className="w-4 h-4 mr-1.5" /> Request Salary Advance
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="dashboard-advance-dialog">
          <DialogHeader><DialogTitle>Request Salary Advance</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div>
              <Label>Amount ₹</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="dashboard-advance-amount" />
            </div>
            <div>
              <Label>Reason</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dashboard-advance-reason" />
            </div>
            <div className="pt-2">
              <div className="text-[11px] uppercase font-bold text-slate-500 mb-1">Previous Requests</div>
              {history.length === 0 ? (
                <div className="text-[11px] text-slate-400 italic">No prior advance requests.</div>
              ) : (
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {history.slice(0, 10).map((h) => (
                    <div key={h.id} className="flex items-center justify-between text-[11.5px] rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-1.5">
                      <span className="font-mono">{INR(h.amount)}</span>
                      <span className="text-slate-500 truncate flex-1 mx-2">{h.reason}</span>
                      <Badge className={h.status === "Approved" ? "bg-emerald-100 text-emerald-800" : h.status === "Rejected" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}>{h.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            <Button onClick={submit} disabled={busy} className="bg-sky-600 hover:bg-sky-700" data-testid="dashboard-advance-submit">
              <Plus className="w-4 h-4 mr-1.5" /> Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


/**
 * v109.9.31 — Dashboard tile version of the Salary-Advance CTA. Mirrors
 * the Approval-Log tile aesthetic (button-styled card, count badges,
 * hover lift) so the two sit side-by-side on the Dashboard.
 */
export function SalaryAdvanceTile() {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/salary-advances/mine");
      setHistory(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadHistory(); }, []);
  useEffect(() => { if (open) loadHistory(); }, [open]);

  const counts = useMemo(() => {
    const approved = history.filter((h) => h.status === "Approved").length;
    const pending = history.filter((h) => h.status === "Pending").length;
    const rejected = history.filter((h) => h.status === "Rejected").length;
    return { approved, pending, rejected };
  }, [history]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      await api.post("/salary/advances", { amount: amt, reason: reason.trim() });
      toast.success("Advance requested");
      setAmount(""); setReason("");
      loadHistory();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="pk-3d w-full text-left rounded-xl border border-slate-200 bg-gradient-to-br from-white to-sky-50/40 hover:from-sky-50 hover:to-white hover:border-sky-300 transition-all p-4"
        data-testid="salary-advance-tile"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <HandCoins size={22} strokeWidth={2} className="text-sky-600 group-hover:scale-110 transition-transform" />
              <div className="font-bold text-slate-900 text-[13px]">Request Salary Advance</div>
            </div>
            <div className="text-[10.5px] text-slate-500 mb-2">
              Ask HR for an advance against your next payroll — click to submit or view history
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">
                <CheckCircle2 size={9} className="mr-1" /> {counts.approved} approved
              </Badge>
              {counts.pending > 0 && (
                <Badge className="bg-amber-100 text-amber-800 text-[10px]">
                  <Clock size={9} className="mr-1" /> {counts.pending} pending
                </Badge>
              )}
              {counts.rejected > 0 && (
                <Badge className="bg-rose-100 text-rose-800 text-[10px]">
                  <XCircle size={9} className="mr-1" /> {counts.rejected} rejected
                </Badge>
              )}
              {history.length === 0 && (
                <Badge className="bg-slate-100 text-slate-600 text-[10px]">No requests yet</Badge>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Total</div>
            <div className="mono font-extrabold text-slate-800 text-2xl leading-none">{history.length}</div>
          </div>
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="salary-advance-tile-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wallet size={17} className="text-sky-600" /> Request Salary Advance</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div>
              <Label>Amount ₹</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="salary-advance-tile-amount" />
            </div>
            <div>
              <Label>Reason</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="salary-advance-tile-reason" />
            </div>
            <div className="pt-2">
              <div className="text-[11px] uppercase font-bold text-slate-500 mb-1">Previous Requests</div>
              {history.length === 0 ? (
                <div className="text-[11px] text-slate-400 italic">No prior advance requests.</div>
              ) : (
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {history.slice(0, 10).map((h) => (
                    <div key={h.id} className="flex items-center justify-between text-[11.5px] rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-1.5">
                      <span className="font-mono">{INR(h.amount)}</span>
                      <span className="text-slate-500 truncate flex-1 mx-2">{h.reason}</span>
                      <Badge className={h.status === "Approved" ? "bg-emerald-100 text-emerald-800" : h.status === "Rejected" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}>{h.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            <Button onClick={submit} disabled={busy} className="bg-sky-600 hover:bg-sky-700" data-testid="salary-advance-tile-submit">
              <Plus className="w-4 h-4 mr-1.5" /> Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
